import { z } from 'zod/v4';

export const MatcherValueDTO = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.array(z.union([z.string(), z.number(), z.boolean(), z.null()])),
]);

export const MatcherMetaDTO = z.object({
  fieldLabel: z.string().nullish(),
  operatorLabel: z.string().nullish(),
  valueLabel: z.string().nullish(),
});

export const MatcherDTO = z.object({
  name: z.string().nullish(),
  target: z.string(),
  operator: z.string(),
  value: MatcherValueDTO.nullish(),
  meta: MatcherMetaDTO.nullish(),
  /** Set by /graph/conditions/validate on matchers that failed validation. */
  error: z.string().nullish(),
});

export type Matcher = z.infer<typeof MatcherDTO>;

export const ConditionDTO = z.union([z.literal('and'), z.literal('or')]);
export type Condition = z.infer<typeof ConditionDTO>;

export interface MatcherWrapper {
  condition: Condition;
  matchers: Array<Matcher | WrapperNode | MatcherWrapper>;
}

export const MatcherWrapperDTO: z.ZodType<MatcherWrapper> = z.lazy(() =>
  z
    .object({
      condition: ConditionDTO as z.ZodType<Condition>,
      matchers: z.array(
        z.union([MatcherDTO, MatcherWrapperDTO, WrapperNodeDTO]),
      ) as z.ZodType<MatcherWrapper['matchers']>,
    })
    .strict(),
);

export const WrapperNodeDTO = z.lazy(() =>
  z.object({
    wrapper: z.array(MatcherWrapperDTO),
  }),
);

export type WrapperNode = z.infer<typeof WrapperNodeDTO>;

/** matcherErrorKey -> message, collected from a failed conditions validation. */
export type MatcherErrorMap = Record<string, string>;

/** nodeId -> MatcherErrorMap, used when validating several TQL nodes independently. */
export type MatcherErrorsByNode = Record<string, MatcherErrorMap>;

/**
 * Content-based key for a matcher. The validate endpoint echoes the sent
 * conditions back with per-matcher `error`s, so rows are matched to errors by
 * content, not position (rows may be edited or reordered in the meantime).
 */
export function matcherErrorKey(matcher: Matcher): string {
  return JSON.stringify([
    matcher.target,
    matcher.operator,
    matcher.value ?? null,
    matcher.name ?? '',
  ]);
}

/** Walks the echoed conditions and collects per-matcher error messages. */
export function collectMatcherErrors(
  conditions?: MatcherWrapper | null,
): MatcherErrorMap {
  const errors: MatcherErrorMap = {};
  if (!conditions?.matchers) return errors;

  const walk = (wrapper: MatcherWrapper) => {
    for (const item of wrapper.matchers ?? []) {
      if ('condition' in item) {
        walk(item);
      } else if ('wrapper' in item) {
        item.wrapper.forEach(walk);
      } else if (item.error) {
        errors[matcherErrorKey(item)] = item.error;
      }
    }
  };

  walk(conditions);
  return errors;
}